import React from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Spin, Alert, Select, Button, Collapse } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';

import MeterReadDetailsTable from '@components/meter-read-details-table/MeterReadDetailsTable';
import MeterReadDetailsScadaTable from '@components/meter-read-details-table/MeterReadDetailsScadaTable';
import AdjustmentsBlade from '@components/blades/meter-details/adjustments-blade/AdjustmentsBlade';
import UpdateMeterDetailsBlade from '@components/blades/meter-details/update-meter-details-blade/UpdateMeterDetailsBlade';
import MeterReadDetailsFeeTable, { FeeAdjustmentsData } from '@components/meter-read-details-table/MeterReadDetailsFeeTable';
import styles from './MeterReadDetailsPage.module.css';
import MeterReadDetailsMainTable from '@components/meter-read-details-table/MeterReadDetailsMainTable';
import MeterReadDetailsMeterSwapTable from '@components/meter-read-details-table/MeterReadDetailsMeterSwapTable';
import { useMeterReadDetailsPage } from './useMeterReadDetailsPage';
import useSession from '@hooks/useSession';
import { getEffectiveMonthYear } from '@utils/effectiveMonthYear';
import { NSPButton } from '@components/ui/NSPButton';
import ExportToExcelButton from '@components/ui/ExportToExcelButton';
import PageLayout from '@components/layout/PageLayout';
import { METER_CONTRACT_YEAR_REPORT } from '@graphql/meterReadDetails.gql';
import { exportContractYearReport } from '@utils/exportContractYearReport';


const { Option } = Select;



const MeterReadDetailsPage = () => {
  const { isManager, isUser, isAdmin, isSuperAdmin } = useSession();
  // Reporter (Windvoice.Reporter) and Approver (Windvoice.Approver) have read-only access — no write operations
  const isReadOnly = (isManager || isAdmin) && !isUser && !isSuperAdmin;

  const [adjustmentsBladeOpen, setAdjustmentsBladeOpen] = React.useState(false);
  const [meterDetailsBladeOpen, setMeterDetailsBladeOpen] = React.useState(false);

  const {
    id,
    tableData,
    loading,
    saving,
    onSaveMonth,
    saveFeedback,
    clearFeedback,
    yearMode,
    setYearMode,
    shouldUseContractView,
    contractStart,
    contractEnd,
    energyBid,
    currentYear,
    navigateYear,
    isNextYearDisabled,
    months,
    refetch,
  } = useMeterReadDetailsPage();

  const effectiveMonthYear = getEffectiveMonthYear();
  const effectiveMonthData = months.find((m) => m.monthYear === effectiveMonthYear) ?? null;

  const [adjBladeMonthYear, setAdjBladeMonthYear] = React.useState(effectiveMonthYear);
  const [meterBladeMonthYear, setMeterBladeMonthYear] = React.useState(effectiveMonthYear);

  // Local fee data store keyed by monthYear. adminFee/forecastFee/counterParty are all
  // server-computed (synced from `months` below).
  const [feeDataByMonth, setFeeDataByMonth] = React.useState<Record<string, FeeAdjustmentsData>>({});

  // adminFee is recomputed server-side on every SCADA/Prod Stat change, forecastFee is
  // populated by an external pipeline, and counterParty is computed at Prod Stat Approval
  // (dollar_value * the meter's Counter Party vendor CIB percentage) — all three must be
  // re-synced from `months` whenever it reloads, or the Fee table keeps showing stale/blank
  // values from local-only state.
  React.useEffect(() => {
    if (!months.length) return;
    setFeeDataByMonth((prev) => {
      const next = { ...prev };
      months.forEach((m) => {
        next[m.monthYear] = {
          counterParty: m.counterPartyFee,
          forecastFee: m.forecastFee,
          adminFee: m.adminFee,
        };
      });
      return next;
    });
  }, [months]);

  // Keep blade months in sync when the effective month recalculates on initial load
  React.useEffect(() => { setAdjBladeMonthYear(effectiveMonthYear); }, [effectiveMonthYear]);
  React.useEffect(() => { setMeterBladeMonthYear(effectiveMonthYear); }, [effectiveMonthYear]);

  const adjBladeMonthData = months.find((m) => m.monthYear === adjBladeMonthYear) ?? null;
  const meterBladeMonthData = months.find((m) => m.monthYear === meterBladeMonthYear) ?? null;

  const adjBladeIsApproved =
    adjBladeMonthData?.approvalStatus === 'Approved' || !!adjBladeMonthData?.invoiceApproved;
  const meterBladeIsApproved =
    meterBladeMonthData?.approvalStatus === 'Approved' || !!meterBladeMonthData?.invoiceApproved;
// ── Overage: derive meter-level flag and projected running total ──────────
  // Use the meter-level flag from MeterYearDetails (spans all contract years).
  // Fall back to scanning the loaded months in case the backend hasn't been
  // updated to expose the top-level field yet.
  const overageEmailSent =
    tableData?.overageEmailSent === true ||
    months.some((m) => m.overageEmailSent === true);

  // For an unapproved current month, running_contract_total is not yet set.
  // Project it as: last approved month's running total + current month net gen.
  const projectedRunningContractTotal = (() => {
    if (!effectiveMonthData) return null;
    if (effectiveMonthData.runningContractTotal != null) {
      return effectiveMonthData.runningContractTotal;
    }
    const lastApproved = [...months]
      .filter(
        (m) => m.approvalStatus === 'Approved' && m.monthYear < effectiveMonthYear,
      )
      .sort((a, b) => b.monthYear.localeCompare(a.monthYear))[0];
    const prevRunning = lastApproved?.runningContractTotal ?? 0;
    const netGen = effectiveMonthData.postProdStatNetGenerationKwh ?? 0;
    return prevRunning + netGen;
  })();

  const [fetchContractYearReport, { loading: exportLoading }] = useLazyQuery(
    METER_CONTRACT_YEAR_REPORT,
    {
      onCompleted: (data) => {
        if (data?.meterContractYearReport) {
          exportContractYearReport(data.meterContractYearReport);
        }
      },
    }
  );

  const handleExportContractYear = () => {
    fetchContractYearReport({ variables: { meterId: Number(id) } });
  };

  const pageTitle = tableData?.meter
    ? `${tableData.meter.meterNumber} - ${tableData.meter.location}`
    : 'Meter Read Details';

  const headerAccessory = tableData ? (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      {yearMode === 'Contract' && (
        <ExportToExcelButton
          onClick={handleExportContractYear}
          disabled={exportLoading}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => navigateYear(-1)}
          className={styles.navBtn}
        />
        <span style={{ minWidth: '60px', textAlign: 'center', fontWeight: 500 }}>
          {currentYear}
        </span>
        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={() => navigateYear(1)}
          disabled={isNextYearDisabled}
          className={styles.navBtn}
        />
      </div>
      <Select
        value={yearMode}
        style={{ width: 180 }}
        onChange={(val) => setYearMode(val)}
      >
        <Option value="Contract">Contract Year</Option>
        <Option value="Calendar">Calendar Year</Option>
      </Select>
    </div>
  ) : null;

  return (
    <PageLayout title={pageTitle} headerAccessoryView={headerAccessory}>
      {loading || !tableData ? (
        <Spin data-testid="spinner" />
      ) : (
        <>

          {yearMode === 'Contract' && (
            <Col className={styles.alertWrapper}>
              <Alert
                type="warning"
                showIcon
                message="The Running totals by Contract Year Report is available by clicking 'Export to Excel'"
              />
            </Col>
          )}

          {saveFeedback && (
            <Col className={styles.alertWrapper}>
              <Alert
                message={saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
                description={saveFeedback.message}
                type={saveFeedback.status}
                showIcon
                closable
                onClose={clearFeedback}
              />
            </Col>
          )}

          <div className={styles.container}>
            <Row>
              <Col span={24}>
                <MeterReadDetailsMainTable
                  id={id}
                  rows={tableData.rows}
                  handleChange={() => { }}
                  saving={saving}
                  updating={false}
                  start={contractStart}
                  end={contractEnd}
                  shouldUseContractView={shouldUseContractView}
                  energyBid={energyBid}
                  isEditable={false}
                  meterType={tableData.meterType ?? null}
                />
              </Col>
            </Row>

            <Collapse
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'adjustments',
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      SPP Dollar Adjustments
                      {!isReadOnly && (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a
                          onClick={(e) => { e.stopPropagation(); clearFeedback(); setAdjustmentsBladeOpen(true); }}
                          style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#1677ff' }}
                        >
                          Adjust
                        </a>
                      )}
                    </span>
                  ),
                  children: (
                    <MeterReadDetailsTable
                      id={id}
                      rows={tableData.adjustmentsRows}
                      handleChange={() => { }}
                      saving={saving}
                      updating={false}
                      start={contractStart}
                      end={contractEnd}
                      shouldUseContractView={shouldUseContractView}
                      isEditable={false}
                    />
                  ),
                },
                {
                  key: 'feeAdjustments',
                  label: 'Invoice Dollar Adjustments',
                  children: (
                    <MeterReadDetailsFeeTable
                      id={id}
                      feeDataByMonth={feeDataByMonth}
                      months={months.map((m) => ({ monthYear: m.monthYear, approvalStatus: m.approvalStatus, invoiceApproved: m.invoiceApproved }))}
                      start={contractStart}
                      end={contractEnd}
                      shouldUseContractView={shouldUseContractView}
                    />
                  ),
                },
                {
                  key: 'scada',
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      Updated Meter Details
                      {!isReadOnly && (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a
                          onClick={(e) => { e.stopPropagation(); clearFeedback(); setMeterDetailsBladeOpen(true); }}
                          style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#1677ff' }}
                        >
                          Update
                        </a>
                      )}
                    </span>
                  ),
                  children: (
                    <MeterReadDetailsScadaTable
                      id={id}
                      rows={tableData.scadaRows}
                      handleChange={() => { }}
                      saving={saving}
                      updating={false}
                      start={contractStart}
                      end={contractEnd}
                      shouldUseContractView={shouldUseContractView}
                      isEditable={false}
                      noDecimals
                    />
                  ),
                },
                {
                  key: 'meterSwap',
                  label: 'Meter Swap Details',
                  children: (
                    <MeterReadDetailsMeterSwapTable
                      id={id}
                      rows={tableData.meterSwapRows ?? []}
                      handleChange={() => { }}
                      saving={saving}
                      updating={false}
                      start={contractStart}
                      end={contractEnd}
                      shouldUseContractView={shouldUseContractView}
                      isEditable={false}
                    />
                  ),
                },
              ]}
            />

            <Row className={styles.buttonContainer}>
              <Col>
                <Link to={`/core-info/${id}`}>
                  <NSPButton size="large" className={styles.coreInfoButton}>
                    View Core Info
                  </NSPButton>
                </Link>
              </Col>
            </Row>
          </div>

        </>
      )}

      <UpdateMeterDetailsBlade
        open={meterDetailsBladeOpen}
        onClose={() => setMeterDetailsBladeOpen(false)}
        id={id ?? ''}
        meterName={tableData?.meter?.location}
        meterNumber={tableData?.meter?.meterNumber}
        monthYear={meterBladeMonthYear}
        monthData={meterBladeMonthData}
        isApproved={meterBladeIsApproved}
        onSaveMonth={onSaveMonth}
        onUploadComplete={refetch}
        onMonthChange={setMeterBladeMonthYear}
        saving={saving}
        saveFeedback={saveFeedback}
      />

<AdjustmentsBlade
        open={adjustmentsBladeOpen}
        onClose={() => setAdjustmentsBladeOpen(false)}
        meterId={id ?? ''}
        meterName={tableData?.meter?.location}
        meterNumber={tableData?.meter?.meterNumber}
        monthYear={adjBladeMonthYear}
        monthData={adjBladeMonthData}
        isApproved={adjBladeIsApproved}
        rateContracts={tableData?.rateContracts ?? []}
        activeRateType={tableData?.activeRateType ?? null}
        energyBid={energyBid}
        runningContractTotal={adjBladeMonthData?.runningContractTotal ?? projectedRunningContractTotal}
        overageEmailSent={overageEmailSent}
        onSaveMonth={onSaveMonth}
        onRateSaved={refetch}
        onMonthChange={setAdjBladeMonthYear}
        saving={saving}
        saveFeedback={saveFeedback}
      />
    </PageLayout>
  );
};

export default MeterReadDetailsPage;
